import React, { CSSProperties, useCallback, useMemo, useState } from 'react';
import { AreaClosed } from '@visx/shape';
import { IconButton } from '@cbhq/cds-web/buttons/IconButton';
import { HStack } from '@cbhq/cds-web/layout';
import { curveStep } from '@visx/curve';
import { scaleLinear } from '@visx/scale';
import { useParentSize } from '@visx/responsive';
import { formatCurrencyPriceValue } from '@/utils/numberFormatter/formatNumbers';
import { BrushSlider } from './BrushSlider';
import { Bound } from './types';
import type { ChartEntry, ZoomLevels, Margins } from './types';

// Dot pattern SVG component for chart background
function DotPattern({ id }: { id: string }) {
  return (
    <pattern id={id} x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
      <circle cx="1" cy="1" r="1" fill="#CED2DB" fillOpacity="0.5" />
    </pattern>
  );
}

export type ChartProps = {
  // to distringuish between multiple charts in the DOM
  id?: string;

  /** Height of the chart in pixels. Defaults to 200. */
  height?: number;

  data: {
    series: ChartEntry[];
    current: number;
  };
  ticksAtLimit: { [bound in Bound]?: boolean | number | undefined };

  styles: {
    area: {
      // color of the ticks in range
      selection: string;
    };

    brush: {
      handle: {
        left: string;
        right: string;
      };
    };
    // vertical line at current price
    priceLine: CSSProperties;

    // bar color of the histogram
    bar: CSSProperties;

    // bottom axis
    axis: CSSProperties;
  };

  margins: Margins;

  interactive?: boolean;

  brushLabels: (d: 'left' | 'right', x: number) => string;
  brushDomain?: [number, number];
  onBrushDomainChange: (domain: [number, number], mode: string | undefined) => void;

  zoomLevels: ZoomLevels;
};

export function DepthChart(props: ChartProps) {
  const {
    data,
    height: containerHeight = 200,
    styles,
    interactive,
    brushDomain,
    onBrushDomainChange,
    zoomLevels,
    brushLabels,
    ticksAtLimit,
  } = props;

  const { series, current } = data;
  // default debounce time is 300ms
  const { parentRef, width, height } = useParentSize();

  // Zoom state
  const [zoomLevel, setZoomLevel] = useState(1);

  const formatTick = useCallback(
    (value: number) => formatCurrencyPriceValue({ price: value, useCompact: true }),
    [],
  );

  const xAccessor = useCallback((d: { price: number }) => d?.price || 0, []);
  const yAccessor = useCallback((d: { activeLiquidity: number }) => d?.activeLiquidity || 0, []);

  // Determine if this is a full range position (both lower and upper ticks are at limit)
  // Full range positions don't need brush handles since they cover the entire price range
  const isFullRange = useMemo(() => {
    return Boolean(ticksAtLimit[Bound.LOWER] && ticksAtLimit[Bound.UPPER]);
  }, [ticksAtLimit]);

  /**
   * Calculate the visible view range for the chart
   *
   * This implementation solves several key issues:
   * 1. Auto-zoom to show brush handles on initial render without constant recalculation
   * 2. Avoid circular dependencies where brush movements trigger view changes
   * 3. Only expand view for non-full-range positions (full range doesn't need brush handles)
   *
   * The view range is calculated as:
   * - Base range: 25% around current price, adjusted by zoom level
   * - Expanded range: If not full range and brush handles exist, expand to include them with padding
   *
   * Key: brushDomain is intentionally NOT in dependencies to prevent constant recalculation
   * when brush handles move. The range only recalculates when price or zoom level changes.
   */
  const viewRange = useMemo(() => {
    if (!current) return undefined;

    // Calculate base zoom range around current price (25% in each direction, adjusted by zoom)
    const rangePercentage = 0.25 / zoomLevel;
    const baseRange = {
      min: Math.max(0, current * (1 - rangePercentage)), // Clamp to 0 - prices can't be negative
      max: current * (1 + rangePercentage),
    };

    let targetRange = baseRange;

    // For non-full-range positions, expand view to include brush handles if they exist
    // This ensures brush handles are visible on initial render but doesn't cause constant updates
    if (!isFullRange && brushDomain?.[0] != null && brushDomain?.[1] != null) {
      const brushMin = Math.min(brushDomain[0], brushDomain[1]);
      const brushMax = Math.max(brushDomain[0], brushDomain[1]);
      targetRange = {
        min: Math.max(0, Math.min(targetRange.min, brushMin)),
        max: Math.max(targetRange.max, brushMax),
      };
    }

    // Always add axis padding so there is visible space outside the selected/current range.
    const targetSpan = Math.max(targetRange.max - targetRange.min, current * 0.01);
    const axisPadding = targetSpan * 0.05;
    return {
      min: Math.max(0, targetRange.min - axisPadding),
      max: targetRange.max + axisPadding,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, zoomLevel]); // Intentionally exclude brushDomain and isFullRange to prevent circular updates
  // Fix the type issue with brushXScale by explicitly typing it
  const brushXScale = useMemo(
    () =>
      scaleLinear<number>()
        .domain(viewRange ? [viewRange.min, viewRange.max] : [0, 1])
        .range([0, width]),
    [width, viewRange],
  );

  // Calculate max liquidity for yScale
  const maxLiquidity = useMemo(() => {
    if (!series.length) return 1;
    return Math.max(...series.map((d) => d.activeLiquidity));
  }, [series]);

  // Add 10% headroom so max points don't clip at the top edge.
  const yDomainMax = useMemo(() => {
    if (!Number.isFinite(maxLiquidity) || maxLiquidity <= 0) return 1;
    return maxLiquidity * 1.1;
  }, [maxLiquidity]);

  // Y scale for the area chart (maps liquidity to pixels)
  const chartHeight = height - 20; // Subtract space for x-axis labels
  const yScale = useMemo(
    () => scaleLinear<number>().domain([0, yDomainMax]).range([chartHeight, 0]),
    [yDomainMax, chartHeight],
  );

  // Memoized accessor functions for AreaClosed to avoid creating new functions in JSX
  const areaXAccessor = useCallback(
    (d: ChartEntry) => brushXScale(xAccessor(d)) ?? 0,
    [brushXScale, xAccessor],
  );
  const areaYAccessor = useCallback(
    (d: ChartEntry) => yScale(yAccessor(d)) ?? 0,
    [yScale, yAccessor],
  );

  // Filter & clamp points to the viewRange for the area chart
  const filteredSeries = useMemo(() => {
    if (!viewRange) return series;
    const pointsBeforeXMin = series.filter((d) => d.price < viewRange.min);
    const closestBeforeXMin =
      pointsBeforeXMin.length > 0
        ? pointsBeforeXMin.reduce((prev, curr) => (curr.price > prev.price ? curr : prev))
        : undefined;

    const pointsAfterXMax = series.filter((d) => d.price > viewRange.max);
    const closestAfterXMax =
      pointsAfterXMax.length > 0
        ? pointsAfterXMax.reduce((prev, curr) => (curr.price < prev.price ? curr : prev))
        : undefined;

    const filteredPoints = series.filter(
      (d) => d.price >= viewRange.min && d.price <= viewRange.max,
    );

    const dataPoints = [
      ...(closestBeforeXMin ? [{ ...closestBeforeXMin, price: viewRange.min }] : []),
      ...filteredPoints,
      ...(closestAfterXMax ? [{ ...closestAfterXMax, price: viewRange.max }] : []),
    ];

    // Add zero points at the actual data boundaries to ensure vertical edges are drawn
    if (dataPoints.length === 0) return dataPoints;

    const firstPrice = dataPoints[0].price;
    const lastPrice = dataPoints[dataPoints.length - 1].price;

    const extendedPoints = [
      { price: firstPrice, activeLiquidity: 0 }, // Start at 0 to draw left vertical edge
      ...dataPoints,
      { price: lastPrice, activeLiquidity: 0 }, // End at 0 to draw right vertical edge
    ];
    return extendedPoints;
  }, [series, viewRange]);

  // ---- Zoom Logic ----
  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev * 2, zoomLevels.max));
  }, [zoomLevels.max]);

  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev * 0.5, zoomLevels.min));
  }, [zoomLevels.min]);

  const handleReset = useCallback(() => {
    setZoomLevel(1);
    onBrushDomainChange([0, 1], 'reset');
  }, [onBrushDomainChange]);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Zoom Controls */}
      <HStack gap={0} testID="zoom-controls">
        <IconButton
          onClick={handleZoomIn}
          variant="secondary"
          name="add"
          transparent
          testID="zoom-in"
          disabled={zoomLevel >= zoomLevels.max}
          active
        />
        <IconButton
          onClick={handleZoomOut}
          variant="secondary"
          name="minus"
          transparent
          testID="zoom-out"
          disabled={zoomLevel <= zoomLevels.min}
        />
        <IconButton
          onClick={handleReset}
          variant="secondary"
          name="refresh"
          transparent
          testID="zoom-reset"
        />
      </HStack>
      {/* Chart Container */}
      <div ref={parentRef} style={{ position: 'relative', width: '100%', height: containerHeight }}>
        {/* Main Chart */}
        <svg width={width} height={height}>
          <defs>
            <DotPattern id="area-dot-pattern" />
            {/* Clip path to restrict drawing to chart area (excluding bottom margin) */}
            <clipPath id="chart-clip">
              <rect x={0} y={0} width={width} height={Math.max(0, height - 20)} />
            </clipPath>
          </defs>

          {/* Gray area fill */}
          <AreaClosed
            data={filteredSeries}
            x={areaXAccessor}
            y={areaYAccessor}
            yScale={yScale}
            curve={curveStep}
            fill={styles.bar?.fill ?? '#EEF0F3'}
            stroke={styles.bar?.stroke ?? '#89909E'}
            strokeWidth={1}
          />

          {/* Dot pattern overlay on top of gray area */}
          <AreaClosed
            data={filteredSeries}
            x={areaXAccessor}
            y={areaYAccessor}
            yScale={yScale}
            curve={curveStep}
            fill="url(#area-dot-pattern)"
            stroke="transparent"
            strokeWidth={0}
          />

          {/* Brush overlay - only show for non-full-range positions */}
          {/* Full range positions cover the entire price range, so brush handles are not needed */}
          <g style={{ pointerEvents: 'all' }}>
            <BrushSlider
              id="chart-brush"
              xScale={brushXScale}
              interactive={interactive ?? false}
              brushLabelValue={brushLabels}
              brushExtent={brushDomain ?? (brushXScale.domain() as [number, number])}
              innerWidth={width}
              innerHeight={height - 20}
              setBrushExtent={onBrushDomainChange}
              leftHandleColor={styles.brush.handle.left}
              rightHandleColor={styles.brush.handle.right}
              isFullRange={isFullRange}
            />
          </g>

          {/* Current price line */}
          <line
            className="visx-line"
            x1={brushXScale(current)}
            y1={0}
            x2={brushXScale(current)}
            y2={height - 20}
            stroke={styles.priceLine?.stroke ?? '#0052FF'}
            strokeWidth={styles.priceLine?.strokeWidth ?? 2}
            pointerEvents="none"
          />
        </svg>

        {/* Custom price labels at edges per Figma */}
        {viewRange && (
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '14px',
              color: '#5B616E',
            }}
          >
            <span>{formatTick(viewRange.min)}</span>
            <span>{formatTick(viewRange.max)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
